/**
 * Byte helpers. Everything returns `Uint8Array<ArrayBuffer>` (not the
 * `ArrayBufferLike` default) so values pass straight into the Web Crypto API,
 * whose `BufferSource` parameters require a plain `ArrayBuffer` backing store.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

const enc = new TextEncoder();
const dec = new TextDecoder();

export const utf8 = {
  encode: (s: string): Bytes => enc.encode(s) as Bytes,
  decode: (b: Uint8Array): string => dec.decode(b),
};

export function toB64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

export function fromB64(b64: string): Bytes {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function toB64Url(bytes: Uint8Array): string {
  return toB64(bytes).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromB64Url(s: string): Bytes {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  return fromB64(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
}

export function concat(...parts: Uint8Array[]): Bytes {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

export function randomBytes(n: number): Bytes {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

/** Retype (copying only if SharedArrayBuffer-backed) for Web Crypto's BufferSource. */
export function bs(u8: Uint8Array): Bytes {
  return (u8.buffer instanceof ArrayBuffer ? u8 : new Uint8Array(u8)) as Bytes;
}
